/**
 * IntakeChrome — coque visuelle partagée 9.N.2.
 *
 * Wrappe TOUS les états de la page `/intake/:token` (Loading, Form, Success,
 * Invalid) — c'est la condition stricte de la parité anti-énumération du
 * cadrage F2 : un attaquant ne peut pas distinguer « lien expiré » /
 * « révoqué » / « forgé » / « quota » / « API down » par les éléments de
 * chrome (top-bar, footer, skip-link).
 *
 * Compose :
 *   - skip-link a11y (visible au focus uniquement)
 *   - top-bar : `<KollectiviaWordmark>` + lang toggle FR/EN
 *   - <main id="intake-content"> conteneur centré max-w-xl
 *   - footer : © Kollectivia + lien privacy
 *
 * Anti-leak strict : aucune métadonnée projet/tenant exposée hors du body
 * spécifique à l'état (l'IntakeForm est le seul à connaître `projectTitle`).
 */

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { KollectiviaWordmark } from "../../../components/brand/KollectiviaMark";

/** URL canonique de la politique de confidentialité Kollectivia. */
export const PRIVACY_NOTICE_URL = "https://kollectivia.com/privacy" as const;

export type IntakeChromeProps = {
  readonly children: React.ReactNode;
  readonly languageDisabled?: boolean;
};

export function IntakeChrome({
  children,
  languageDisabled = false,
}: IntakeChromeProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLang: "fr" | "en" = i18n.resolvedLanguage === "en" ? "en" : "fr";

  function setLang(lang: "fr" | "en"): void {
    if (languageDisabled) return;
    if (lang === currentLang && searchParams.get("lang") === lang) return;
    // The metadata response resolves the supported content language before rendering.
    // Persiste le choix dans l'URL pour respecter la priorité 1 du cadrage F2 §3.6
    // (?lang=… > LanguageDetector > "fr") en cas de refresh / partage du lien.
    const next = new URLSearchParams(searchParams);
    next.set("lang", lang);
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 pb-[env(safe-area-inset-bottom)]">
      <a
        href="#intake-content"
        className="focus-visible:ring-brand-500 sr-only rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-900 shadow-md focus-visible:not-sr-only focus-visible:absolute focus-visible:left-4 focus-visible:top-4 focus-visible:z-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("public_intake.page.skip_link")}
      </a>

      <header className="mx-auto flex w-full max-w-xl items-center justify-between px-4 py-5 sm:py-6">
        <KollectiviaWordmark />
        <nav aria-label="Language" className="flex items-center gap-0.5 text-xs">
          <button
            type="button"
            disabled={languageDisabled}
            onClick={() => setLang("fr")}
            aria-pressed={currentLang === "fr"}
            lang="fr"
            className={`focus-visible:ring-brand-500 touch-manipulation rounded-md px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              currentLang === "fr"
                ? "font-semibold text-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            FR
          </button>
          <span aria-hidden="true" className="text-slate-300">
            ·
          </span>
          <button
            type="button"
            disabled={languageDisabled}
            onClick={() => setLang("en")}
            aria-pressed={currentLang === "en"}
            lang="en"
            className={`focus-visible:ring-brand-500 touch-manipulation rounded-md px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              currentLang === "en"
                ? "font-semibold text-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            EN
          </button>
        </nav>
      </header>

      <main id="intake-content" role="main" className="mx-auto w-full max-w-xl flex-1 px-4 pb-8">
        {children}
      </main>

      <footer className="mx-auto flex w-full max-w-xl items-center justify-between gap-4 px-4 py-6 text-xs text-slate-500">
        <span translate="no">© Kollectivia</span>
        <a
          href={PRIVACY_NOTICE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="focus-visible:ring-brand-500 rounded-md px-1 underline-offset-2 transition-colors hover:text-slate-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("public_intake.chrome.footer.privacy_link")}
        </a>
      </footer>
    </div>
  );
}
